import { PlayerInfoSection } from '@/components/common'
import { People, Person } from '@/components/icons'
import { LockedRating, PlayerUnlockOverlay, useUnlockGate } from '@/components/profile-limit'
import { useRatingLock } from '@/hooks/useRatingLock'

import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'

import { Player, Rating } from '@/types'
import { useNavigate } from 'react-router-dom'

/**
 * The rating to show in a search-result pill: the primary rating, falling back
 * to the provisional rating when the primary is missing or NR.
 */
function renderRating(primaryRating: string | undefined, provisionalRating: number | undefined): string | undefined {
  const missingPrimary = primaryRating == null || primaryRating === 'NR'
  const hasProvisional = provisionalRating != null && provisionalRating !== 0
  if (missingPrimary && hasProvisional) return provisionalRating.toString()
  return primaryRating
}

/** The pill's display value for one format off the player's ratings. */
function pillValue(ratings: Rating | undefined, kind: 'doubles' | 'singles'): string | undefined {
  const provisional = ratings?.provisionalRatings
  if (kind === 'doubles') return renderRating(ratings?.doubles, provisional?.doublesRating)
  return renderRating(ratings?.singles, provisional?.singlesRating)
}

const RatingPill = ({ player, kind, locked }: { player: Player; kind: 'doubles' | 'singles'; locked: boolean }) => {
  return (
    <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
      {kind === 'doubles' ? <People className="text-navy-900" /> : <Person className="text-navy-900" />}
      <span className="text-xs text-navy-900 font-medium">
        {locked ? <LockedRating /> : pillValue(player?.ratings, kind)}
      </span>
    </div>
  )
}

/** Both rating pills for a search row, locked together by the viewer's quota. */
const PlayerRatingPills = ({ player }: { player: Player }) => {
  const isRatingLocked = useRatingLock()(player?.id)
  return (
    <div className="flex gap-x-1 items-center">
      <RatingPill player={player} kind="doubles" locked={isRatingLocked} />
      <RatingPill player={player} kind="singles" locked={isRatingLocked} />
    </div>
  )
}

type Props = {
  className?: string
  player: Player
  isFilter?: boolean
  /** Gate profile navigation behind the profile-views unlock flow. */
  enableProfileUnlock?: boolean
}

export const PlayerItem = ({ className, player, isFilter, enableProfileUnlock }: Props) => {
  const navigate = useNavigate()

  const onNavigateProfile = () => {
    navigate(`${ROUTE_PLAYER_PROFILE}/${player.id}`, { relative: 'path' })
  }

  const { gated, promptOpen, closePrompt, handlePress, handleUnlock } = useUnlockGate(
    player?.id,
    !!enableProfileUnlock,
    onNavigateProfile
  )

  const widthClass = isFilter ? 'lg:max-w-[432px]' : 'lg:max-w-[640px]'
  const backgroundClass = gated ? 'bg-gray-100' : 'bg-white'

  return (
    <div
      className={`relative flex p-4 justify-between xl:max-w-[640px] 2xl:max-w-[720px] cursor-pointer ${widthClass} ${backgroundClass} rounded-2xl ${className}`}
      onClick={handlePress}
    >
      <PlayerInfoSection player={player} />
      <PlayerRatingPills player={player} />
      {promptOpen && <PlayerUnlockOverlay onUnlock={handleUnlock} onDismiss={closePrompt} />}
    </div>
  )
}
